import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {ActivityIndicator, StyleSheet, View} from 'react-native';
import {useQuery} from '@tanstack/react-query';
import {useTranslation} from 'react-i18next';

import {fetchPersonnelList} from '@/entities/personnel/api';
import type {PersonnelRecord} from '@/entities/personnel/types';
import {faceState} from '@/entities/personnel/face';
import {PersonAvatar} from '@/entities/personnel/ui/PersonAvatar';
import {PressCard} from '@/shared/ui/card';
import {Icon} from '@/shared/ui/icons';
import {EmptyState} from '@/shared/ui/empty-state';
import {useContentInsets} from '@/shared/ui/screen';
import {SplitView} from '@/shared/ui/split-view';
import {DataList} from '@/shared/ui/data-list';
import {Cell, NameCell, type Column} from '@/shared/ui/table';
import {FilterSelect} from '@/shared/ui/filter-bar';
import {sortParam, type SortAccessors} from '@/shared/lib/table-sort';
import {intParam, stringParam, useParamState} from '@/shared/lib/use-param-state';
import {SkeletonList} from '@/shared/ui/skeleton';
import {StatusPill} from '@/shared/ui/status';
import {AppText} from '@/shared/ui/text';
import {spacing, useTheme} from '@/shared/ui/theme';
import {useBreakpoint} from '@/shared/lib/breakpoints';

/**
 * The household's people, list only — creating or editing a person is out of scope for
 * the Home context (see the plan: people arrive from the terminal sync). Each row shows
 * the person's enrolled face through `PersonAvatar` — the one path every list in the
 * cabinet uses — falling back to initials when there is no face. Search is server-side,
 * debounced so a keystroke is not a request.
 *
 * The list no longer prints its own "Люди" heading: the app header already names the
 * section, and the same word twice, 40pt apart, was the screen's biggest wasted band.
 * What sits there instead is the search bar, which is what the screen is actually for
 * once there are more than a dozen people.
 *
 * Past the desktop breakpoint it stops being a list of cards at all. A card is an
 * object you look AT — a face, a name, a badge, 90pt tall with air around it — and on a
 * 1440pt window that meant eight people on screen where a TABLE shows forty, with the
 * columns a manager actually scans (department, face enrolled, arrival) finally in
 * columns, comparable down the page. The chosen row opens in a pane beside the list
 * instead of replacing it, so scanning forty people and reading one of them stop being
 * forty round trips.
 *
 * The phone is untouched: same cards, same push-a-screen navigation, because at 390pt
 * there is room for exactly one thing and a table is a horizontal scroll.
 */
const PER_PAGE = 20;
/* Кодеки адреса — модульные, чтобы ссылка на них была стабильной. */
const NAME_SORT = {key: 'name', direction: 'asc'} as const;
const SORT_PARAM = sortParam(NAME_SORT);
const PAGE_PARAM = intParam(1);
const PER_PAGE_PARAM = intParam(PER_PAGE);
/** Размеры страницы для настольной сетки — рейка под таблицей их и предлагает. */
const PAGE_SIZES = [20, 50, 100] as const;

/** Порядки, которые предлагаются чипами на узком окне, и подписи к ним. */
const PHONE_SORTS = ['name', 'position', 'department', 'arrived'] as const;
const PHONE_SORT_LABELS: Record<string, string> = {
  name: 'home.people.colName',
  position: 'home.people.colPosition',
  department: 'home.people.colDepartment',
  arrived: 'home.people.colArrived',
};

export type PeopleListProps = {
  companyId: string;
  /** Phone: open the record on its own screen. Not used on desktop — see `renderDetail`. */
  onOpen: (recordId: string, name: string) => void;
  /** Optional control under the search box — e.g. the company's "+ Сотрудник". */
  headerAction?: React.ReactNode;
  /**
   * The desktop toolbar's own verbs. Defaults to `headerAction`, but the two want
   * different faces: a phone header wants a filled button under a thumb, a console
   * strip wants flat verbs beside flat verbs. A caller with both passes both.
   */
  toolbarActions?: React.ReactNode;
  /**
   * Desktop: what to show in the pane beside the table. The two hosts pass different
   * cards — Home the read-only one, the company the managed one with its actions —
   * which is why the list does not import either itself.
   */
  renderDetail?: (recordId: string, name: string) => React.ReactNode;
  /**
   * Extra grid columns, desktop only, inserted after «Отдел» and before the two terse
   * status columns at the right edge.
   *
   * It exists for the company's «Оклад», which this component must not know about: the
   * same list is Home's roster, where there is no payroll and no `salaries` right to
   * read one with. Passing the column in keeps that knowledge in the screen that has
   * the permission, and keeps this file free of a `salaries` import it would have to
   * gate on every render.
   *
   * Phone cards are unaffected on purpose — at 390pt a card already carries a name, a
   * position and a face badge, and a fourth line of money is the one that would push
   * the row past a thumb's worth of scanning.
   */
  extraColumns?: Column<PersonnelRecord>[];
};

const styles = StyleSheet.create({
  header: {gap: spacing(3), paddingTop: spacing(3), paddingBottom: spacing(3)},
  // Wide: the search bar and the create action share one line. A full-bleed "+
  // Сотрудник" across 1100pt reads as a banner, not a button.
  headerRow: {flexDirection: 'row', alignItems: 'center', gap: spacing(3)},
  headerSearch: {flex: 1},

  // Два факта одной строкой: размер состава и та дыра, из-за которой позвонят в
  // поддержку.
  deck: {flexDirection: 'row', alignItems: 'center', gap: spacing(2), paddingTop: spacing(1)},
  deskList: {gap: spacing(3), paddingBottom: spacing(6)},
  /* The console's list screen: one docked panel filling the workspace column. */
  deskFrame: {
    flex: 1,
    minHeight: 0,
    width: '100%',
    alignSelf: 'center',
    paddingBottom: spacing(4),
  },
  deskScroll: {padding: spacing(4)},
  list: {
    gap: spacing(2),
    paddingBottom: spacing(6),
    width: '100%',
    alignSelf: 'center',
    flexGrow: 1,
  },
  listEmpty: {justifyContent: 'center'},
  columns: {gap: spacing(3)},
  cell: {flex: 1},
  footer: {paddingVertical: spacing(5)},

  row: {flexDirection: 'row', alignItems: 'center', gap: spacing(3)},
  body: {flex: 1, gap: 2},
  faceMark: {flexDirection: 'row', alignItems: 'center', gap: spacing(1)},
});

/**
 * One person. The avatar carries a teal notch when they arrived today; the face badge
 * is a pill only when a face IS enrolled — its absence is the normal case and does not
 * deserve a mark of its own in a list.
 *
 * Memoised, and taking `onOpen` rather than a ready-made `onPress`: a fresh arrow per
 * render would change the props on every parent render and the memo would never hold.
 * Callers therefore pass a STABLE onOpen (useCallback) — see PeopleScreen.
 */
const PersonRow = React.memo(function PersonRow({
  person,
  companyId,
  onOpen,
}: {
  person: PersonnelRecord;
  companyId: string;
  onOpen: (recordId: string, name: string) => void;
}) {
  const {t} = useTranslation();
  const p = useTheme();
  // A compact row carries the positive badge only: a list is scanned for who
  // IS ready, and "no" and "not reported" both mean "not that person" here. The
  // card distinguishes them, which is where the distinction is actionable.
  const face = faceState(person);

  return (
    <PressCard
      padding={3}
      onPress={() => onOpen(person.record_id, person.full_name)}
      accessibilityLabel={person.full_name}>
      <View style={styles.row}>
        <PersonAvatar
          companyId={companyId}
          recordId={person.record_id}
          revision={person.revision}
          name={person.full_name}
          badgeTone={person.arrived_today ? 'online' : null}
        />
        <View style={styles.body}>
          <AppText variant="subtitle" numberOfLines={1}>
            {person.full_name}
          </AppText>
          {person.position ? (
            <AppText variant="caption" numberOfLines={1}>
              {person.position}
            </AppText>
          ) : null}
        </View>
        {/* Three states, like the desktop column — the card used to draw the
            first and nothing for the other two, so "we know there is no face"
            and "an old terminal never said" looked identical. The amber one is
            the actionable set and the only one that takes a colour. */}
        {face === 'yes' ? (
          <StatusPill
            tone="granted"
            size="sm"
            dot={false}
            label={t('home.people.faceBadge')}
          />
        ) : face === 'no' ? (
          <StatusPill
            tone="pending"
            size="sm"
            dot={false}
            // `faceNo` is "Нет" and reads fine UNDER a column headed «Лицо».
            // On a card there is no column, so the chip has to say the whole
            // thing — the same words the deck above it counts.
            label={t('home.people.deckNoFace')}
          />
        ) : null}
        <Icon name="chevron" size={14} color={p.mutedForeground} />
      </View>
    </PressCard>
  );
});

export function PeopleList({
  companyId,
  onOpen,
  headerAction,
  toolbarActions,
  renderDetail,
  extraColumns,
}: PeopleListProps) {
  const {t} = useTranslation();
  const p = useTheme();
  const insets = useContentInsets();
  const {columns, isDesktop} = useBreakpoint();
  /*
    Отбор и порядок живут В АДРЕСЕ: список сотрудников — это вопрос, которым делятся
    («вот у кого нет лица») и в который возвращаются кнопкой «назад». Открытая справа
    карточка остаётся жестом: у неё есть свой маршрут `/staff/:recordId`.
    См. `shared/lib/use-param-state`.
  */
  const [search, setSearch] = useParamState('q', '', stringParam);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [department, setDepartment] = useParamState('department', '', stringParam);
  const [face, setFace] = useParamState('face', '', stringParam);
  const [arrived, setArrived] = useParamState('arrived', '', stringParam);
  const [perPage, setPerPage] = useParamState('per', PER_PAGE, PER_PAGE_PARAM);
  const [pageParam, setPageParam] = useParamState('page', 1, PAGE_PARAM);
  const [sortState, setSortState] = useParamState('sort', NAME_SORT, SORT_PARAM);

  /*
    ОДИН запрос на весь состав — и отсюда всё остальное.

    Раньше здесь было две серверные разбивки: страница на десктопе и бесконечная лента
    на телефоне. Обе честно листали, но обе делали невозможным главное — фильтр и
    сортировку. Отсортировать двадцать привезённых строк и нарисовать стрелку в
    заголовке значит соврать: «первый по алфавиту» окажется первым на этой странице, а
    не в составе из четырёхсот человек. Фильтр «без лица» поверх такой страницы
    показал бы «3», когда в компании их тридцать.

    `GET /control/personnel` БЕЗ `per_page` отдаёт весь список без лимита — это
    задокументированное поведение маршрута (docs/api/conventions.md §«Страницы»: при
    отсутствии значения гейтвей передаёт `0`, и лимита нет вообще). На том же стоят
    «Устройства». Поэтому состав приезжает целиком, а листает его клиент — и рейка под
    таблицей это не скрывает.
  */
  const query = useQuery({
    queryKey: ['personnel-all', companyId],
    queryFn: () => fetchPersonnelList(companyId),
  });

  const items = useMemo(() => query.data?.data ?? [], [query.data]);
  const isPending = query.isPending;
  const isError = query.isError;
  const refetch = () => {
    void query.refetch();
  };

  /** Отделы, которые ВСТРЕЧАЮТСЯ в составе: справочник тут был бы длиннее правды. */
  const departmentOptions = useMemo(
    () => [
      {value: '', label: t('actions.any')},
      ...Array.from(new Set(items.flatMap(r => r.department_names).filter(Boolean)))
        .sort((a, b) => a.localeCompare(b))
        .map(name => ({value: name, label: name})),
    ],
    [items, t],
  );

  const needle = search.trim().toLowerCase();
  const matched = useMemo(
    () =>
      items.filter(r => {
        if (department && !r.department_names.includes(department)) {
          return false;
        }
        // Три состояния лица, а не два: запись, о которой терминал никогда не
        // отчитывался, — это НЕ «нет лица». См. `faceState`.
        if (face && faceState(r) !== face) {
          return false;
        }
        if (arrived === 'yes' && !r.arrived_today) {
          return false;
        }
        if (arrived === 'no' && r.arrived_today) {
          return false;
        }
        if (!needle) {
          return true;
        }
        return [r.full_name, r.position, r.employee_number, ...r.department_names]
          .filter(Boolean)
          .some(field => String(field).toLowerCase().includes(needle));
      }),
    [items, department, face, arrived, needle],
  );

  /*
    Сортировка — по всему составу, а не по видимой странице, и поэтому честная. Стрелку
    получают ВСЕ столбцы, включая отдел, лицо и приход: сервер их упорядочивать не
    умеет, но он тут больше и не нужен.
  */
  const ACCESSORS: SortAccessors<PersonnelRecord> = useMemo(
    () => ({
      name: r => r.full_name,
      number: r => r.employee_number || null,
      position: r => r.position || null,
      department: r => r.department_names[0] ?? null,
      // «Насколько лицо есть»: столбец нажимают, чтобы собрать тех, у кого его НЕТ,
      // а общее правило открывает признак от большего.
      face: r => (faceState(r) === 'yes' ? 0 : faceState(r) === 'no' ? 2 : 1),
      arrived: r => !!r.arrived_today,
    }),
    [],
  );
  // Новый отбор — новая первая страница: седьмая страница прежнего к нему отношения
  // не имеет, и оставлять читателя на пустой седьмой нельзя.
  useEffect(() => {
    setPageParam(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [needle, department, face, arrived, perPage, sortState]);

  const dirty = !!department || !!face || !!arrived;

  const filters = (
    <>
      {departmentOptions.length > 1 ? (
        <FilterSelect
          label={t('home.people.colDepartment')}
          value={department}
          options={departmentOptions}
          onChange={setDepartment}
          phone={!isDesktop}
          testID="people-department"
        />
      ) : null}
      <FilterSelect
        label={t('home.people.colFace')}
        value={face}
        options={[
          {value: '', label: t('actions.any')},
          {value: 'yes', label: t('home.people.faceBadge')},
          {value: 'no', label: t('home.people.deckNoFace')},
          {value: 'unknown', label: t('home.people.faceUnknown')},
        ]}
        onChange={setFace}
        phone={!isDesktop}
        testID="people-face"
      />
      <FilterSelect
        label={t('home.people.colArrived')}
        value={arrived}
        options={[
          {value: '', label: t('actions.any')},
          {value: 'yes', label: t('home.people.arrivedYes')},
          {value: 'no', label: t('home.people.arrivedNo')},
        ]}
        onChange={setArrived}
        phone={!isDesktop}
        testID="people-arrived"
      />
    </>
  );

  /** Та же строка там, где столбцов нет. Раскладку в несколько колонок ведёт список. */
  const renderCard = useCallback(
    (item: PersonnelRecord) => (
      <PersonRow person={item} companyId={companyId} onOpen={onOpen} />
    ),
    [onOpen, companyId],
  );

  /*
    Пустых состояний два, и они отвечают на разные вопросы. «В составе никого» — это
    про данные, и тогда сбрасывать фильтр бессмысленно. «Под отбор никто не подошёл» —
    это про отбор, и тогда бессмысленно заводить сотрудника.
  */
  const failed = (
    <EmptyState
      tone="error"
      title={t('home.people.error')}
      actionLabel={t('actions.retry')}
      onAction={() => {
        refetch();
      }}
    />
  );

  const cols: Column<PersonnelRecord>[] = [
    {
      key: 'name',
      // Стрелку получают ВСЕ столбцы: состав приезжает целиком, сортирует клиент, и
      // «первый по алфавиту» означает первого в составе, а не на этой странице.
      sortKey: 'name',
      title: t('home.people.colName'),
      flex: 2.2,
      render: r => (
        <NameCell
          mark={
            <PersonAvatar
              companyId={companyId}
              recordId={r.record_id}
              revision={r.revision}
              name={r.full_name}
              size={28}
            />
          }>
          {r.full_name}
        </NameCell>
      ),
    },
    {
      key: 'number',
      sortKey: 'number',
      title: t('home.people.colNumber'),
      width: 104,
      minWidth: 1280,
      render: r => (
        <Cell mono tone={p.mutedForeground}>
          {r.employee_number || '—'}
        </Cell>
      ),
    },
    {
      key: 'position',
      sortKey: 'position',
      title: t('home.people.colPosition'),
      flex: 1.6,
      render: r => <Cell tone={p.mutedForeground}>{r.position || '—'}</Cell>,
    },
    {
      key: 'department',
      sortKey: 'department',
      title: t('home.people.colDepartment'),
      flex: 1.4,
      minWidth: 1180,
      render: r => (
        <Cell tone={p.mutedForeground}>{r.department_names[0] ?? '—'}</Cell>
      ),
    },
    ...(extraColumns ?? []),
    {
      key: 'face',
      sortKey: 'face',
      title: t('home.people.colFace'),
      width: 76,
      // Three states in a column narrow enough for one word each: enrolled,
      // not enrolled, and nothing reported. The last is a dash, not "нет" —
      // an old terminal's people must not read as people without faces.
      render: r => {
        const state = faceState(r);
        if (state === 'yes') {
          return (
            <StatusPill
              tone="granted"
              size="sm"
              dot={false}
              label={t('home.people.faceBadge')}
            />
          );
        }
        return (
          <Cell tone={p.mutedForeground}>
            {t(state === 'no' ? 'home.people.faceNo' : 'home.people.faceUnknown')}
          </Cell>
        );
      },
    },
    {
      key: 'arrived',
      sortKey: 'arrived',
      title: t('home.people.colArrived'),
      width: 86,
      align: 'right',
      render: r =>
        r.arrived_today ? (
          <Cell tone={p.signalInk}>{t('home.people.arrivedYes')}</Cell>
        ) : (
          <Cell tone={p.mutedForeground}>—</Cell>
        ),
    },
  ];

  const total = items.length;
  const missing = items.filter(r => faceState(r) === 'no').length;

  /*
    Тот же список, что на «Явке», — и это главное, что здесь изменилось.

    Раньше состав жил в пристыкованной панели: свои действия в тулбаре, свои счётчики
    в подвале, своя рейка страниц, а на телефоне вместо всего этого — лента с
    собственной шапкой. Два разных устройства для одной и той же работы: найти
    человека, упорядочить по столбцу, пролистать. Теперь устройство одно
    (`shared/ui/data-list`), а список задаёт только то, что у него своё: чем
    фильтровать, как выглядит столбец и как выглядит карточка.
  */
  const list = (
    <DataList<PersonnelRecord>
      testID="people"
      rows={matched}
      total={items.length}
      columns={cols}
      keyOf={r => r.record_id}
      accessors={ACCESSORS}
      initialSort={NAME_SORT}
      sortControl={{value: sortState, onChange: setSortState}}
      pageControl={{value: pageParam, onChange: setPageParam}}
      perPageControl={{value: perPage, onChange: setPerPage}}
      perPageOptions={PAGE_SIZES}
      onRowPress={r => setSelectedId(r.record_id)}
      selectedKey={selectedId}
      actions={toolbarActions ?? headerAction}
      search={{
        value: search,
        onChange: setSearch,
        placeholder: t('home.people.search'),
      }}
      filters={filters}
      dirty={dirty}
      onReset={() => {
        setDepartment('');
        setFace('');
        setArrived('');
      }}
      loading={isPending}
      skeleton={<SkeletonList count={8} />}
      empty={isError ? failed : <EmptyState icon="people" title={t('home.people.empty')} />}
      noMatches={
        isError ? failed : (
          <EmptyState
            icon="people"
            title={t(dirty || needle ? 'home.people.emptyFiltered' : 'home.people.empty')}
          />
        )
      }
      cardColumns={columns}
      phoneSorts={PHONE_SORTS}
      sortLabel={key => t(PHONE_SORT_LABELS[key] ?? key)}
      renderCard={renderCard}
      footer={
        <>
          <AppText variant="caption">
            {t('home.people.deckTotal')}: {total}
          </AppText>
          <AppText variant="caption" color={missing > 0 ? p.warningInk : undefined}>
            {t('home.people.deckNoFace')}: {missing}
          </AppText>
          {query.isFetching ? (
            <ActivityIndicator size="small" color={p.primary} />
          ) : null}
        </>
      }
    />
  );

  if (isDesktop) {
    const selected = items.find(r => r.record_id === selectedId) ?? null;
    return (
      <View style={[styles.deskFrame, insets]}>
        <SplitView
          list={list}
          detail={
            selected && renderDetail
              ? renderDetail(selected.record_id, selected.full_name)
              : null
          }
          detailKey={selected?.record_id ?? null}
          detailTitle={selected?.full_name}
          onCloseDetail={() => setSelectedId(null)}
        />
      </View>
    );
  }

  return <View style={[styles.list, insets]}>{list}</View>;
}
